// @flow

import * as React from 'react';
import {BodyMono} from 'sail/Text';
import Group from 'sail/Group';
import type {Command, Param} from '../commands/resource';
import {flagsRegex} from '../../terminal/command';

export const data_regex_whole_quotes = /(?<tag>[^=]+)=(?<value>[^"]+)/g;
export const data_regex_front_quotes = /"(?<tag>[^"]+)"=(?<value>\S+)/g;
export const data_regex_back_quotes = /"(?<tag>[^"]+)"="(?<value>[^"]+)"/g;
export const white_space = /\s\s+/g;

// Return tag part of a parameter.
export function handleTag(rawParam: string, params_config: Param): string {
  const normalized = rawParam.toLowerCase();
  let param = '';

  // shorthand (-)
  if (normalized.slice(0, 1) === '-' && normalized.slice(0, 2) !== '--') {
    param =
      Object.entries(params_config).find(
        ([_, v]) =>
          typeof v === 'object' &&
          v != null &&
          v.hasOwnProperty('shorthand') &&
          v.shorthand === normalized.slice(1),
      )?.[0] ?? '';
  }
  // normal (--)
  else if (normalized.slice(0, 2) === '--' && normalized.slice(2, 3) !== '-') {
    param = params_config[normalized.slice(2)] ? normalized.slice(2) : '';
  }
  return param;
}

// Return tag part of a parameter.
function getTagWord(rawParam: string, params_config: Param): string {
  const normalized = rawParam.toLowerCase();
  let param = '';

  // shorthand (-)
  if (normalized.slice(0, 1) === '-' && normalized.slice(0, 2) !== '--') {
    param = Object.entries(params_config).find(
      ([_, v]) =>
        typeof v === 'object' &&
        v != null &&
        v.hasOwnProperty('shorthand') &&
        v.shorthand === normalized.slice(1),
    )?.[0];
    if (param) {
      param = `--${param}`;
    } else {
      param = normalized;
    }
  } else {
    param = normalized;
  }
  return param;
}

// Return the value of a parameter.
function handleValue(parts: RegExp$matchResult): string {
  let value =
    parts && parts.groups && parts.groups.value_sQuote
      ? parts.groups.value_sQuote
      : '';
  value =
    !value && parts && parts.groups && parts.groups.value_dQuote
      ? parts.groups.value_dQuote
      : value;
  value =
    !value && parts && parts.groups && parts.groups.value_noQuote
      ? parts.groups.value_noQuote
      : value;
  value =
    !value && parts && parts.groups && parts.groups.value_data
      ? parts.groups.value_data
      : value;
  return value.trim();
}

// Return the tag and value of input given as data.
function handleData(input_value: string): [string, string] {
  const user_input = input_value;
  let tag = '';
  let value = '';

  let data_match = data_regex_back_quotes.exec(user_input);
  data_regex_back_quotes.exec('');
  if (data_match && data_match.groups) {
    tag = data_match.groups.tag;
    value = data_match.groups.value;
  } else {
    data_match = data_regex_front_quotes.exec(user_input);
    data_regex_front_quotes.exec('');
    if (data_match && data_match.groups) {
      tag = data_match.groups.tag;
      value = data_match.groups.value;
    } else {
      data_match = data_regex_whole_quotes.exec(user_input);
      data_regex_whole_quotes.exec('');
      if (data_match && data_match.groups) {
        tag = data_match.groups.tag;
        value = data_match.groups.value;
      }
    }
  }
  return [tag, value];
}

// Parses parameter/values list into an Object with key/value pairs
export function parseParams(
  raw: Array<string>,
  params_config: Param,
  current_node: Command,
  require_valid: boolean,
): [{[string]: string}, Array<string>] {
  const input = [...raw];
  if (input.length === 0) {
    return [{}, []];
  }

  const params = {};
  const tags = [];
  let expand_count = 0;
  input.forEach(function(item) {
    const parts = flagsRegex.exec(item);
    flagsRegex.exec('');

    if (!parts || matchedRegex(parts, current_node)) {
      return;
    }

    let param = '';
    if (require_valid) {
      param = handleTag(
        parts.groups ? parts.groups.flag.trim() : '',
        params_config,
      );
    } else {
      param = getTagWord(
        parts.groups ? parts.groups.flag.trim() : '',
        params_config,
      );
    }

    const input_value = handleValue(parts);
    if (param === 'data' || param === '--data') {
      const [tag, value] = handleData(input_value);
      if (tag) {
        params[tag] = value;
        tags.push('--data');
        tags.push(tag);
      }
    } else if (param === 'expand') {
      params[`expand[${expand_count}]`] = input_value;
      expand_count += 1;
    } else if (param) {
      params[param] = input_value;
      tags.push(param);
    } else {
      throw new Error(`Unsupported parameter: ${param}`);
    }
  });

  return [params, tags];
}

export function paramRequiresValue(
  param: string,
  current_node: Command,
): boolean {
  return current_node.params &&
    handleTag(param, current_node.params) &&
    current_node.params[handleTag(param, current_node.params)]
    ? current_node.params[handleTag(param, current_node.params)].no_value !==
        true
    : true;
}

export function matchedRegex(
  parts: RegExp$matchResult,
  current_node: Command,
): boolean {
  return (
    !parts ||
    !parts.groups ||
    !parts.groups.flag ||
    (!parts.groups.value_sQuote &&
      !parts.groups.value_dQuote &&
      !parts.groups.value_noQuote &&
      !parts.groups.value_data &&
      paramRequiresValue(parts.groups.flag, current_node))
  );
}

export function parse(input: string): Array<string> {
  return input.split(/\s+/g);
}

export function normalizeCommand(input: string): string {
  return input
    .replace(/\\/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Display the spinner while waiting.
export const Spinner = ({text = ''}: {text: string}) => {
  const FRAMES = '⣾⣽⣻⢿⡿⣟⣯⣷';
  const FPS = 12;

  const [frameIdx, setFrameIdx] = React.useState(0);
  const requestRef = React.useRef();
  const timeoutRef = React.useRef();

  React.useEffect(() => {
    const cycle = () => {
      timeoutRef.current = setTimeout(() => {
        setFrameIdx((state) => (state + 1) % FRAMES.length);
        requestRef.current = requestAnimationFrame(cycle);
      }, 1000 / FPS);
    };

    requestRef.current = requestAnimationFrame(cycle);

    return () => {
      if (requestRef && requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }

      if (timeoutRef && timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <BodyMono color="gray200">
      <Group spacing={2} direction="horizontal">
        <span style={{marginRight: 8}}>{FRAMES[frameIdx]}</span>
        {text}
      </Group>
    </BodyMono>
  );
};
